import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import * as crypto from 'crypto-js'
import { BehaviorSubject, finalize, firstValueFrom, retry } from 'rxjs'

import { environment } from '../../../environments/environment'
import { LoginRequest } from '../../pages/login/types/login.type'
import { SKIP_AUTH_HEADER } from '../tokens/http-context.token'
import { LoginResponse, UserData } from '../types/auth.type'
import { BetConfigEntry } from '../types/bet.type'
import { ApiResponse } from '../types/response.type'
import { mergeRenewedUserFields } from '../utils/user-data.util'
import { BetConfigService } from './bet-config.service'
import { DomainService } from './domain.service'
import { HelperService } from './helper.service'
import { WebsocketService } from './websocket.service'

const API_URL = environment.apiUrl

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	#loginResponse: LoginResponse | null = null

	isTokenRefreshing$ = new BehaviorSubject<boolean>(false)

	isCasinoBlocked$ = new BehaviorSubject<boolean>(false)

	isAsianCasinoBlocked$ = new BehaviorSubject<boolean>(false)

	constructor(
		private readonly httpClient: HttpClient,
		private readonly helperService: HelperService,
		private readonly websocketService: WebsocketService,
		private readonly domainService: DomainService,
		private readonly translateService: TranslateService,
		private readonly betConfigService: BetConfigService,
	) {
		this.isCasinoBlocked$.next(this.helperService.getUserData()?.user_fields?.IS_BLOCKED_CASINO === 'true')
		this.isAsianCasinoBlocked$.next(this.helperService.getUserData()?.user_fields?.IS_BLOCKED_ASIAN_CASINO === 'true')
	}

	get loginResponse() {
		return this.#loginResponse
	}

	set setLoginResponse(loginResponse: LoginResponse | null) {
		this.#loginResponse = loginResponse
	}

	/**
	 * Login a user
	 * @param data
	 */
	login(data: Partial<LoginRequest>) {
		return this.httpClient.post<LoginResponse>(
			`${API_URL}/api/auth/user_login_v2`,
			{
				USERNAME: data.username,
				PASSWORD: data.password,
				DOMAIN_NAME: this.domainService.domain,
				...(data.securityToken ? { SECURITY_TOKEN: data.securityToken } : {}),
			},
			{
				context: new HttpContext().set(SKIP_AUTH_HEADER, true),
			},
		)
	}

	/**
	 * Login a user as a guest / demo user
	 */
	demoLogin() {
		return this.httpClient.post<LoginResponse>(
			`${API_URL}/api/auth/demo_login`,
			{
				DOMAIN_NAME: this.domainService.domain,
			},
			{
				context: new HttpContext().set(SKIP_AUTH_HEADER, true),
			},
		)
	}

	/**
	 * Register a new user
	 * @param data
	 */
	register(data: Record<string, string | null | undefined>) {
		return this.httpClient.post<ApiResponse<string[] | string>>(
			`${API_URL}/api/self_register`,
			{
				...data,
				DOMAIN_NAME: this.domainService.domain,
			},
			{
				context: new HttpContext().set(SKIP_AUTH_HEADER, true),
			},
		)
	}

	/**
	 * Refresh the access token
	 */
	refreshToken() {
		this.isTokenRefreshing$.next(true)

		this.httpClient.post<UserData>(`${API_URL}/auth/renew`, {}).subscribe({
			next: (response) => {
				const currentContent = this.helperService.getUserData()
				if (!currentContent) return
				const rawUser = response.user_fields ?? (response as unknown as Record<string, unknown>)['user']
				const userFields = mergeRenewedUserFields(rawUser, currentContent.user_fields)

				this.helperService.setUserData({ ...currentContent, ...response, user_fields: userFields })

				const lang = userFields.LANGUAGE || 'en'
				this.translateService.use(lang)
				this.helperService.setSelectedLanguage(lang)
				this.helperService.clearTranslateCookie()
			},
			error: () => {
				this.logOut()
			},
		})
	}

	/**
	 * Log the user out
	 */
	async logOut() {
		this.websocketService.sendMessage({ topics: [], topicMode: 'replace' })
		await firstValueFrom(
			this.httpClient.post(`${API_URL}/api/auth/logout`, {}).pipe(
				retry({ delay: 2000, resetOnSuccess: true, count: 3 }), // Retry 3 times every two seconds when logout fails
				finalize(() => {
					this.clearUserData()
					window.location.href = '/home'
				}),
			),
		)
	}

	isDemoUser(): boolean {
		const demoCredential = `demo-${this.domainService.domain.replace('.', '')}`
		const username = this.helperService.getUserData()?.user_fields?.NAME
		return username === 'demo' || username === demoCredential
	}

	/**
	 * Get the access token from the preferred token storing mechanism
	 */
	getAccessToken() {
		return this.helperService.getUserData()?.access_token ?? ''
	}

	/**
	 * Get the refresh token from the preferred token storing mechanism
	 */
	getRefreshToken() {
		return this.helperService.getUserData()?.refresh_token ?? ''
	}

	clearUserData() {
		sessionStorage.removeItem('app')
		sessionStorage.removeItem('walletId')
		sessionStorage.removeItem('rate')
		sessionStorage.removeItem('lastActivityTime') // Clear session timeout data
		localStorage.removeItem('language')
		this.translateService.use('en')
		this.helperService.clearTranslateCookie()
		this.betConfigService.clearBetConfig()
	}

	encrypt(plaintext: string) {
		return crypto.AES.encrypt(plaintext, environment.rememberMeKey).toString()
	}

	decrypt(cypherText: string) {
		return crypto.AES.decrypt(cypherText, environment.rememberMeKey).toString(crypto.enc.Utf8)
	}

	/**
	 * Sets and processes user data after a successful login response.
	 * Parses specific user fields and updates the application's user data object.
	 *
	 * @param {LoginResponse} loginResponse - The response received after a user logs in, containing user details and custom fields.
	 * @return {void} No return value.
	 */
	setUserDataAfterLogin(loginResponse: LoginResponse): void {
		const userBetSettings = JSON.parse(loginResponse.user_fields.USER_BETSLIP)
		const whiteLabelBetSettings = JSON.parse(loginResponse.user_fields.WHITE_LABEL_BET_SLIP)
		const app: UserData = {
			...loginResponse,
			user_fields: {
				...loginResponse.user_fields,
				USER_BETSLIP: userBetSettings,
				WHITE_LABEL_BET_SLIP: whiteLabelBetSettings,
				BET_CONFIG: loginResponse.user_fields.BET_CONFIG
					? (JSON.parse(loginResponse.user_fields.BET_CONFIG) as Record<string, BetConfigEntry>)
					: undefined,
			},
		}
		this.helperService.setUserData(app)
		if (loginResponse.user_fields.BET_CONFIG) {
			this.betConfigService.updateBetConfig(loginResponse.user_fields.BET_CONFIG)
		}
		const lang = loginResponse.user_fields.LANGUAGE || 'en'
		this.translateService.use(lang)
		this.helperService.setSelectedLanguage(lang)
		this.helperService.clearTranslateCookie()

		this.isCasinoBlocked$.next(loginResponse.user_fields.IS_BLOCKED_CASINO === 'true')
		this.isAsianCasinoBlocked$.next(loginResponse.user_fields.IS_BLOCKED_ASIAN_CASINO === 'true')
		this.setLoginResponse = null
	}
}
